/**
 * Logstream interface.
 * Works kind of like a Backbone.Collection, with bonus!
 *
 * Usage:
 *
 *     // Get the static/global logstream collection object
 *     dc = require('collections/v5/logstream');
 *
 *     // Open a connection
 *     dc.connect();
 *
 *     // Start listening to a device's logs
 *     dc.add(device);
 *
 *     // Listen for log events
 *     dc.on('log', function(data) { do something with data });
 *
 *     // Listen for a specific device
 *     dc.on('devicegroup_id:<id>' function(data) { do something with data });
 *
 *     // Listen for a specific log type
 *     dc.on('log_type:<id>' function(data) { do something with data });
 *
 * In other words, this collection is a collection of all the
 * devices you are listening to. The event stream object is abstracted
 * away somewhat. Don't overwrite logstream.es
 *
 * All log events come in as JSON, which we convert to an Object.
 * Contents of the data passed back from the EventSource
 * have the following keys:
 *  {
 *      "devicegroup_id": <devicegroup_id>,
 *      "ts": <ISO timestamp>,
 *      "log_type": <{ development, um don't know }>,
 *      "type": <{ agent.log, don't know }>,
 *      "msg": <log message>
 *  }
 *
 */
import _ from 'underscore';
import Backbone from 'backbone';
import Radio from 'backbone.radio';
import v5_Model from '@/models/v5/model';
import Api from '@/lib/api/api';
import _event_source_polyfill from 'event-source-polyfill';
    var dc = new Backbone.Collection();
    /**
     * Throttle timeout in milliseconds. Requests to log multiple devices 
     * will be separated by this much time in order to not trigger 429 errors
     * from querying the API too frequently.
     */
    var LISTEN_THROTTLE_MS = 200;

    /**
     * A list of devices we will attempt to listen to as soon as the
     * connection is active
     */
    dc.connection_queue = [];
    /**
     * Until this is TRUE, place all device requests in the connection_queue
     * This won't be set to true until we see an 'opened' event from the API.
     */
    dc.allow_device_requests = false;
    /**
     * Return the URL used to retrieve the key we need for
     * the event stream
     */
    function url(devicegroup_id) { return Api.urls.device_group_connections({devicegroup_id: devicegroup_id}); }

    /**
     * Listen for EventStream messages and trigger Backbone Events
     * from the logstream collection. External modules interested in the
     * logs themselves should be listening to the events triggered in this 
     * function.
     */
    function onmessage(e) {
        // TODO: create dc.trigger for event stream messages
        var connection_data = JSON.parse(e.data);
        dc.trigger('connection_stream', connection_data);
    }

    /**
     * Listen for 'state_change' event and change aspects of the logstream
     * based on them. Namely, when we get a close event, we need to clean 
     * up the EventSource object and reset the state of the logstream. 
     *
     * When we get an open event, allow device log requests to go through and
     * request any queued device logs which may have been requested before 
     * we got the opened state.
     */
    function on_state_change(e) {
        var event = JSON.parse(e.data);
        dc.trigger('state', event.state, event);
        if (event.device_id) {
            dc.trigger('connection_stream', event);
        }
        switch (event.state) {
        case 'closed':
            dc.allow_device_requests = false;
            dc.reset();
            dc.es = null;
            break;
        case 'opened':
            dc.allow_device_requests = true;
            break;
        }
    }

    /**
     * Create a new Eventsource based on data we got
     * from the logstream endpoint.
     *
     * Set up an event listener that triggers Backbone Events
     *
     */
    function create_es(data) {
        if (dc.es) {
            dc.es = null;
        }
        if (data.access_token) {
            handle_reconnect();
            return;
        }
       if ( !data || !data.data ) {
            close_eventstream();
            return;
       }

        dc.es = new EventSource(data.data.attributes.url);

        // Capture and handle events such as "open" or "close"
        dc.es.addEventListener('state_change', on_state_change);

        // Capture and handle general messages
        dc.es.onmessage = onmessage;
    }

    function close_eventstream() {
        if (!dc.es) return;
        dc.es.close();
        dc.es = null;
    }

    /**
     * Get the Eventstream URL and create a new connection
     */
    function connect() {
        start_network_listeners();
        return Api.ajax_v5({
            url: dc.url(),
            method: 'POST'
        }).done(create_es);
    }

    /**
     * Close the event stream manually
     */
    function disconnect() {
        close_eventstream();
        return dc.reset();
    }

    /**
     * Tell the API to start sending device connection data for a specific devicegroup
     */
    var start_listening = _.throttle(function start_listening(device_group) {
        return Api.ajax_v5({
            url: dc.url(device_group.id),
            headers: {
                'Content-type': 'application/vnd.api+json'
            },
            method: 'POST',
        }).done(create_es);
    }, LISTEN_THROTTLE_MS);

    /**
     * start listening to the device group with event stream
     */
    function on_add(device_group) {
        start_listening(device_group);
    }
    function handle_disconnect() {
        close_eventstream();

        Radio.request('alerts', 'showAlert', {
            message: "Lost internet connection. Live logs will no longer display.",
            messageType: 'danger',
            timeout: 5000
        });
        
        handle_reconnect();
    }
    function handle_reconnect(){
        window.addEventListener('online', function () {

            dc.connect().then(function(){
                if (dc.models.length) {
                    _.forEach(dc.models, start_listening);
                }
            },
            function(err){
                console.warn('Could not connect event stream', err);
            });

            Radio.request('alerts', 'showAlert', {
                message: "Regained internet connection.",
                messageType: 'success',
                timeout: 5000
            });
            window.removeEventListener('online', handle_reconnect);
        });
    }
    function start_network_listeners() {
        window.removeEventListener('offline', handle_disconnect);
        window.addEventListener('offline', handle_disconnect);
    }
    /**
     * When a device is added to the collection, tell the EventStream
     * to send us logs for that device
     */
    dc.on('add', on_add);

    /**
     * Public interface
     */
    _.extend(dc, {
        url: url,
        model: v5_Model,
        es: null,
        connect: connect,
        disconnect: disconnect
    });

    export default dc;


